import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchJson, postJson } from './adminApi';
import { duration, fmt, formatDateTime, jobKindLabel } from './adminFormat';
import { jobStatsLine } from './OverviewTab';
import buildInfo from '../buildInfo.json';

// Non-secret custom proxy fields are remembered per browser; the password never is.
const PROXY_STORAGE_KEY = 'admin.proxy';
const PROXY_KEEP = ['server', 'username', 'ports'];
const LOG_TAIL_LINES = 150;
const PROGRESS_COUNTERS = [
    ['new', 'New'], ['updated', 'Updated'], ['unchanged', 'Unchanged'], ['relocated', 'Moved'],
    ['removed_missing', 'Removed'], ['detail_filled', 'Detail filled'], ['enriched', 'Enriched'],
    ['removed', 'Old rows removed'], ['months_complete', 'Months complete'],
    ['pages', 'Pages'], ['failed_pages', 'Failed pages'], ['requests', 'Requests'], ['cloudflare_blocked', 'Cloudflare blocks'],
];

const readSavedProxy = () => {
    try {
        const saved = JSON.parse(window.localStorage.getItem(PROXY_STORAGE_KEY) || '{}');
        return Object.fromEntries(PROXY_KEEP.map((key) => [key, String(saved[key] || '')]));
    } catch (error) {
        return { server: '', username: '', ports: '' };
    }
};

const statusClass = (status) => `ax-status ax-status--${status || 'unknown'}`;

const JobForm = ({ jobs, running, onStarted }) => {
    const configured = jobs?.proxy;
    const hasDefault = Boolean(configured && !configured.error);
    const [kind, setKind] = useState('full_scan');
    const [allowMassDelete, setAllowMassDelete] = useState(false);
    // Hours to spread a full scan over; empty = the default pace (about an hour).
    const [hours, setHours] = useState('');
    const [proxyMode, setProxyMode] = useState(null);
    const [proxy, setProxy] = useState(() => ({ ...readSavedProxy(), password: '' }));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    // "Default" is preselected: the backend's own proxy (.env, else the one saved below).
    const mode = proxyMode || (hasDefault || !configured ? 'default' : 'off');
    const proxyFields = () => ({
        server: proxy.server.trim(), username: proxy.username.trim(), password: proxy.password, ports: proxy.ports.trim(),
    });
    const rememberProxyFields = () => {
        try {
            window.localStorage.setItem(PROXY_STORAGE_KEY, JSON.stringify(
                Object.fromEntries(PROXY_KEEP.map((key) => [key, proxy[key].trim()])),
            ));
        } catch (storageError) {
            // Not remembered: fine.
        }
    };

    const runProxyAction = async (action, confirmText) => {
        if (confirmText && !window.confirm(confirmText)) return;
        setBusy(true);
        setError('');
        try {
            await action();
            onStarted();
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };
    const handleSaveDefault = () => runProxyAction(async () => {
        await fetchJson('/admin/jobs/proxy', { method: 'PUT', body: JSON.stringify(proxyFields()) });
        rememberProxyFields();
        setProxy((prev) => ({ ...prev, password: '' }));
        setProxyMode('default');
    });
    const handleRemoveDefault = () => runProxyAction(
        () => fetchJson('/admin/jobs/proxy', { method: 'DELETE' }),
        'Remove the saved default proxy? Jobs then connect directly unless .env sets a proxy.',
    );

    const setProxyField = (event) => setProxy((prev) => ({ ...prev, [event.target.name]: event.target.value }));

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (mode === 'default' && !hasDefault) {
            setError('No default proxy is set yet: pick Custom, enter the proxy and click "Save as default".');
            return;
        }
        const massDelete = kind === 'full_scan' && allowMassDelete;
        const spreadHours = kind === 'full_scan' && hours.trim() ? Number(hours.replace(',', '.')) : null;
        if (spreadHours !== null && !(spreadHours > 0 && spreadHours <= 12)) {
            setError('Hours must be a number between 0 and 12, or empty for the default pace.');
            return;
        }
        const message = kind === 'sold'
            ? 'Fetch sold listings and recompute the area prices per m²?'
            : `Start a full scan of all of Sweden${spreadHours ? `, spread over ${spreadHours} h` : ''}?${massDelete ? '\n\nMass delete is ON: the listings are replaced even if the scan finds fewer than half as many as the database holds.' : ''}`;
        if (!window.confirm(message)) return;
        const body = { kind, proxy: { mode } };
        if (massDelete) body.allow_mass_delete = true;
        if (spreadHours) body.hours = spreadHours;
        if (mode === 'custom') {
            Object.assign(body.proxy, proxyFields());
            rememberProxyFields();
        }
        setBusy(true);
        setError('');
        try {
            await postJson('/admin/jobs', body);
            setAllowMassDelete(false);
            onStarted();
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const handleStop = async () => {
        if (!window.confirm('Stop the running job? It stops between two requests; what is scraped so far is kept.')) return;
        setBusy(true);
        setError('');
        try {
            await postJson('/job/abort');
            onStarted();
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <form className="ax-job-form" onSubmit={handleSubmit}>
            <fieldset className="ax-segmented">
                <legend>Job</legend>
                <label>
                    <input type="radio" name="job_kind" value="full_scan" checked={kind === 'full_scan'} onChange={() => setKind('full_scan')} />
                    Active listings
                </label>
                <label>
                    <input type="radio" name="job_kind" value="sold" checked={kind === 'sold'} onChange={() => setKind('sold')} />
                    Sold listings
                </label>
            </fieldset>
            {kind === 'full_scan' ? (
                <>
                    <p className="ax-hint ax-hint--tight">
                        Full scan of all of Sweden: every active listing is fetched, RegSO areas are assigned as pages come in,
                        and the listings are replaced with the new ones at the end. Runs every second night at 04:00, spread over 3 hours.
                    </p>
                    <label className="ax-field ax-field--hours">
                        Spread over (hours)
                        <input
                            type="number"
                            inputMode="decimal"
                            min="0.5"
                            max="12"
                            step="0.5"
                            value={hours}
                            onChange={(event) => setHours(event.target.value)}
                            placeholder="Default (about 1 h)"
                        />
                    </label>
                    <label className="ax-check">
                        <input type="checkbox" checked={allowMassDelete} onChange={(event) => setAllowMassDelete(event.target.checked)} />
                        Allow mass delete (replace even if the scan finds under half as many listings)
                    </label>
                </>
            ) : (
                <p className="ax-hint ax-hint--tight">
                    Sold listings month by month: every month of the last four years not fetched completely yet, plus the last
                    three months. Then RegSO areas and the area prices per m² (the last year, or up to four years until an area
                    has 20 sales). Runs after the nightly job on the last day of each month.
                </p>
            )}
            <fieldset className="ax-segmented">
                <legend>Proxy</legend>
                <label>
                    <input type="radio" name="proxy_mode" value="default" checked={mode === 'default'} onChange={() => setProxyMode('default')} />
                    Default
                </label>
                <label>
                    <input type="radio" name="proxy_mode" value="off" checked={mode === 'off'} onChange={() => setProxyMode('off')} />
                    Off (direct)
                </label>
                <label>
                    <input type="radio" name="proxy_mode" value="custom" checked={mode === 'custom'} onChange={() => setProxyMode('custom')} />
                    Custom
                </label>
            </fieldset>
            {mode !== 'custom' ? (
                <p className="ax-hint ax-hint--tight">
                    {!configured && (
                        <>No default proxy yet. Pick Custom, enter the proxy once and click &quot;Save as default&quot;.</>
                    )}
                    {configured?.error && <span className="ax-error">{configured.error}</span>}
                    {hasDefault && (
                        <>
                            Default proxy ({configured.source === 'env' ? 'from .env' : 'saved on the server'}):{' '}
                            <code>{configured.label}</code>
                            {configured.ports && <> · rotating ports <code>{configured.ports}</code></>}
                            {configured.source === 'saved' && (
                                <>
                                    {' · '}
                                    <button type="button" className="ax-link" onClick={handleRemoveDefault} disabled={busy}>Remove</button>
                                </>
                            )}
                        </>
                    )}
                </p>
            ) : (
                <div className="ax-proxy-fields">
                    <label className="ax-field ax-wide">
                        Server
                        <input name="server" value={proxy.server} onChange={setProxyField} placeholder="http://proxy.example.com:20001" autoComplete="off" spellCheck="false" />
                    </label>
                    <label className="ax-field">
                        Username
                        <input name="username" value={proxy.username} onChange={setProxyField} autoComplete="off" spellCheck="false" />
                    </label>
                    <label className="ax-field">
                        Password
                        <input type="password" name="password" value={proxy.password} onChange={setProxyField} autoComplete="new-password" />
                    </label>
                    <label className="ax-field ax-wide">
                        Rotate ports
                        <input name="ports" value={proxy.ports} onChange={setProxyField} placeholder="optional, e.g. 20001-20010" autoComplete="off" />
                    </label>
                    <div className="ax-actions ax-wide">
                        <button type="button" className="ax-button ax-button--secondary" onClick={handleSaveDefault} disabled={busy}>
                            Save as default
                        </button>
                    </div>
                    <p className="ax-hint ax-hint--tight ax-wide">
                        Start job uses this proxy for one job only. &quot;Save as default&quot; stores it on the server (readable only by
                        the backend) for every job, the nightly scan included. The password is never shown again or written to the
                        job history or log.
                        {configured?.source === 'env' && ' Note: the proxy in .env takes precedence over a saved default.'}
                    </p>
                </div>
            )}
            <p className="ax-warning">
                {kind === 'sold'
                    ? 'A month only replaces the stored sales for that month after a clean run that saw ≥ 98% of the source’s total.'
                    : 'Listings no longer for sale are only removed after a clean run that saw ≥ 98% of the source’s total.'}
            </p>
            <div className="ax-actions">
                <button type="submit" className="ax-button" disabled={busy || running}>Start job</button>
                <button type="button" className="ax-button ax-button--danger" disabled={busy || !running} onClick={handleStop}>Stop running job</button>
            </div>
            {error && <p className="ax-error" role="alert">{error}</p>}
        </form>
    );
};

// One live number with a bar: e.g. listings scanned out of the source's total.
const LiveCounter = ({ label, value, total }) => {
    const count = Number(value) || 0;
    const max = Number(total) || 0;
    const share = max ? Math.min(100, Math.round((100 * count) / max)) : 0;
    return (
        <div className="ax-live-counter">
            <div className="ax-tile-label">{label}</div>
            <div className="ax-live-value">
                {fmt(count)}
                <span className="ax-muted"> / {max ? fmt(max) : '…'}</span>
            </div>
            <div className="ax-progress" aria-label={`${label}: ${share}%`}>
                <span className="ax-progress-fill" style={{ width: `${share}%` }} />
                <span className="ax-progress-label">{max ? `${share}%` : 'waiting for the first page'}</span>
            </div>
        </div>
    );
};

const CurrentJob = ({ jobs }) => {
    const current = jobs?.current;
    const status = jobs?.status || {};
    const progress = status.progress || {};
    if (!current) {
        const last = jobs?.history?.find((job) => job.status !== 'running');
        return (
            <dl className="ax-kv">
                <dt>Status</dt>
                <dd>
                    No job running.
                    {last && <> Last: <span className={statusClass(last.status)}>{last.status}</span> ({formatDateTime(last.finished || last.started)})</>}
                </dd>
                {status.last_error && (<><dt>Last error</dt><dd className="ax-error">{status.last_error}</dd></>)}
            </dl>
        );
    }
    const counters = PROGRESS_COUNTERS.filter(([key]) => Number.isFinite(progress[key]) && progress[key] > 0);
    return (
        <>
            <dl className="ax-kv">
                <dt>Status</dt>
                <dd><span className={statusClass('running')}>running</span>{current.abort_requested ? ' (stopping…)' : ''}</dd>
                <dt>Job</dt><dd>{jobKindLabel(current)}</dd>
                <dt>Proxy</dt><dd>{current.proxy || 'off'}</dd>
                <dt>Elapsed</dt><dd>{duration(current.elapsed_s)}</dd>
                <dt>Phase</dt><dd>{progress.phase || '—'}{progress.message ? ` · ${progress.message}` : ''}</dd>
            </dl>
            {current.kind !== 'sold' && (
                <div className="ax-live">
                    <LiveCounter label="Listings scanned" value={progress.seen} total={progress.total_count} />
                    <LiveCounter label="RegSO areas covered" value={progress.regso_covered} total={progress.regso_total} />
                </div>
            )}
            {counters.length > 0 && (
                <div className="ax-pills">
                    {counters.map(([key, label]) => <span key={key} className="ax-pill">{label}: {fmt(progress[key])}</span>)}
                </div>
            )}
        </>
    );
};

const JobLog = ({ jobs, selectedLog, onSelectLog }) => {
    const [logFiles, setLogFiles] = useState([]);
    const [content, setContent] = useState('');
    const [full, setFull] = useState(false);
    const [error, setError] = useState('');
    const logRef = useRef(null);
    const runningLog = jobs?.current ? jobs?.status?.log_file : null;
    const defaultLog = runningLog || jobs?.history?.[0]?.log || logFiles[0]?.name || '';
    const logName = selectedLog || defaultLog;

    const loadFiles = useCallback(() => {
        fetchJson('/admin/scrape-logs')
            .then((payload) => setLogFiles(Array.isArray(payload?.logs) ? payload.logs : (Array.isArray(payload) ? payload : [])))
            .catch(() => {});
    }, []);

    useEffect(loadFiles, [loadFiles, runningLog]);

    const loadLog = useCallback(async () => {
        if (!logName) return;
        try {
            const query = full ? 'full=1' : `tailLines=${LOG_TAIL_LINES}`;
            const payload = await fetchJson(`/admin/scrape-logs/${encodeURIComponent(logName)}?${query}`);
            const el = logRef.current;
            const atBottom = !el || el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
            setContent(String(payload?.content || ''));
            setError('');
            if (atBottom) window.requestAnimationFrame(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; });
        } catch (err) {
            setError(err.message);
        }
    }, [full, logName]);

    // Follow the running job's log while it runs.
    useEffect(() => {
        loadLog();
        if (!runningLog || logName !== runningLog) return undefined;
        const timer = window.setInterval(loadLog, 3000);
        return () => window.clearInterval(timer);
    }, [loadLog, logName, runningLog]);

    const names = logFiles.map((file) => file.name);
    if (logName && !names.includes(logName)) names.unshift(logName);

    return (
        <>
            <div className="ax-log-head">
                <h4>Log</h4>
                <select value={logName} onChange={(event) => onSelectLog(event.target.value)} aria-label="Log file">
                    {names.length === 0 && <option value="">No logs yet</option>}
                    {names.map((name) => (
                        <option key={name} value={name}>{name === runningLog ? `● ${name}` : name}</option>
                    ))}
                </select>
                <label className="ax-check ax-check--small">
                    <input type="checkbox" checked={full} onChange={(event) => setFull(event.target.checked)} />
                    Full file
                </label>
            </div>
            {error && <p className="ax-error">{error}</p>}
            <pre className="ax-log" ref={logRef}>{content || 'No log yet. Start a job and its output appears here.'}</pre>
        </>
    );
};

// Redeploy: the Pi stops the API, pulls main from GitHub and starts it again (backend admin_deploy.py).
// While it restarts the API doesn't answer; a new process start time means it's back.
const DEPLOY_POLL_MS = 3000;
const DEPLOY_TIMEOUT_MS = 4 * 60 * 1000;

const DeployCard = ({ running }) => {
    const [info, setInfo] = useState(null);
    const [phase, setPhase] = useState('idle'); // idle | waiting | done | timeout
    const [error, setError] = useState('');
    const [before, setBefore] = useState(null);
    const waitRef = useRef(null);

    const load = useCallback(() => fetchJson('/admin/deploy').then((payload) => {
        setInfo(payload);
        return payload;
    }), []);

    useEffect(() => {
        load().catch((err) => setError(err.message));
        return () => window.clearTimeout(waitRef.current);
    }, [load]);

    const waitForRestart = (previous, deadline) => {
        waitRef.current = window.setTimeout(async () => {
            try {
                const payload = await load();
                if (payload.started_at && payload.started_at !== previous.started_at) {
                    setPhase('done');
                    return;
                }
            } catch (err) {
                // Down while it restarts: keep waiting.
            }
            if (Date.now() > deadline) setPhase('timeout');
            else waitForRestart(previous, deadline);
        }, DEPLOY_POLL_MS);
    };

    const handleRedeploy = async () => {
        const message = running
            ? 'A job is running and will be stopped.\n\nRedeploy anyway? The API stops, pulls the latest from GitHub and starts again (down for about 10–30 seconds).'
            : 'Redeploy the backend? The API stops, pulls the latest from GitHub and starts again (down for about 10–30 seconds).';
        if (!window.confirm(message)) return;
        setError('');
        try {
            const previous = info || await load();
            await postJson('/admin/deploy', running ? { force: true } : {});
            setBefore(previous);
            setPhase('waiting');
            waitForRestart(previous, Date.now() + DEPLOY_TIMEOUT_MS);
        } catch (err) {
            setError(err.message);
        }
    };

    const commit = info?.commit;
    const changed = before?.commit?.hash && commit?.hash && before.commit.hash !== commit.hash;
    return (
        <section className="ax-card">
            <h3>Backend</h3>
            <dl className="ax-kv">
                <dt>Running</dt>
                <dd>
                    {commit ? <><code>{commit.hash}</code> {commit.subject}</> : '—'}
                    {commit?.committed_at && <small className="ax-muted"> · {formatDateTime(commit.committed_at)}</small>}
                </dd>
                <dt>Started</dt>
                <dd>{info?.started_at ? formatDateTime(info.started_at) : '—'}</dd>
            </dl>
            <div className="ax-actions">
                <button type="button" className="ax-button" onClick={handleRedeploy} disabled={phase === 'waiting'}>
                    {phase === 'waiting' ? 'Redeploying…' : 'Redeploy backend'}
                </button>
            </div>
            <p className="ax-hint ax-hint--tight">
                Stops the API on the Pi, pulls the latest from GitHub (installing new packages if requirements.txt changed)
                and starts it again. The Pi also pulls by itself every 5 minutes when there is something new.
            </p>
            {phase === 'waiting' && <p className="ax-warning" role="status">Restarting: waiting for the API to come back…</p>}
            {phase === 'done' && (
                <p className="ax-success" role="status">
                    Redeployed{changed ? <>: <code>{before.commit.hash}</code> → <code>{commit.hash}</code></> : ' (no new commits; restarted)'}.
                </p>
            )}
            {phase === 'timeout' && (
                <p className="ax-error" role="alert">
                    The API hasn&apos;t restarted after 4 minutes. Check the log below, and that omradeskollen-redeploy.path is installed on the Pi (backend README).
                </p>
            )}
            {error && <p className="ax-error" role="alert">{error}</p>}
            {info?.log && (
                <details className="ax-deploy-log">
                    <summary>Update log</summary>
                    <pre className="ax-log">{info.log}</pre>
                </details>
            )}
        </section>
    );
};

// The commit this frontend bundle was built from (baked in at build time by scripts/gen-build-info.js).
// It reflects the build the browser is actually running, so after a push it tells you whether Cloudflare
// has served the new version yet — compare it with the latest commit and reload until it matches.
const FrontendCard = () => {
    const hasInfo = Boolean(buildInfo && buildInfo.hash);
    return (
        <section className="ax-card">
            <h3>Frontend</h3>
            <dl className="ax-kv">
                <dt>Running</dt>
                <dd>
                    {hasInfo
                        ? <><code>{buildInfo.hash}</code> {buildInfo.subject}</>
                        : <span className="ax-muted">dev build (not built from a deploy)</span>}
                    {buildInfo?.committedAt && <small className="ax-muted"> · {formatDateTime(buildInfo.committedAt)}</small>}
                </dd>
                {buildInfo?.builtAt && (
                    <>
                        <dt>Built</dt>
                        <dd>{formatDateTime(buildInfo.builtAt)}</dd>
                    </>
                )}
            </dl>
            <p className="ax-hint ax-hint--tight">
                The commit this page was built from — the build Cloudflare is serving you right now. After a
                push, reload until this matches the latest commit to confirm the new version has gone live.
            </p>
        </section>
    );
};

const JobsTab = ({ jobs, onRefreshJobs }) => {
    const [selectedLog, setSelectedLog] = useState('');
    const running = Boolean(jobs?.current);
    const history = jobs?.history || [];

    return (
        <>
            <div className="ax-grid ax-grid--two">
                <section className="ax-card">
                    <h3>Start a scrape job</h3>
                    <JobForm jobs={jobs} running={running} onStarted={() => { setSelectedLog(''); onRefreshJobs(); }} />
                </section>
                <section className="ax-card">
                    <h3>Current job</h3>
                    <CurrentJob jobs={jobs} />
                    <JobLog jobs={jobs} selectedLog={selectedLog} onSelectLog={setSelectedLog} />
                </section>
            </div>
            <DeployCard running={running} />
            <FrontendCard />
            <section className="ax-card">
                <h3>Job history</h3>
                <div className="ax-table-scroll">
                    <table className="ax-table">
                        <thead>
                            <tr><th>Started</th><th>Job</th><th>Status</th><th className="num">Duration</th><th>Result</th><th>Log</th></tr>
                        </thead>
                        <tbody>
                            {history.length === 0 && <tr><td colSpan={6}>No jobs yet.</td></tr>}
                            {history.map((job) => (
                                <tr key={job.id}>
                                    <td>{formatDateTime(job.started)}</td>
                                    <td>
                                        {jobKindLabel(job)}
                                        {job.options?.scheduled && <small className="ax-muted"> · scheduled</small>}
                                        {job.options?.allow_mass_delete && <small className="ax-muted"> · mass delete</small>}
                                        {job.options?.hours && <small className="ax-muted"> · {job.options.hours} h</small>}
                                        {job.proxy && job.proxy !== 'off' && <><br /><small className="ax-muted">via {job.proxy}</small></>}
                                    </td>
                                    <td><span className={statusClass(job.status)}>{job.status}</span></td>
                                    <td className="num">{duration(job.duration_s ?? job.elapsed_s)}</td>
                                    <td className="ax-wrap">{job.error ? <span className="ax-error">{job.error}</span> : jobStatsLine(job.stats)}</td>
                                    <td>
                                        {job.log && (
                                            <button type="button" className="ax-link" onClick={() => setSelectedLog(job.log)}>View log</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </>
    );
};

export default JobsTab;
